import { AfterViewInit, Component, computed, EventEmitter, Input, OnDestroy, Output, Signal, signal, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Subscription } from 'rxjs';
import type { SignatureInfo } from '@contentauth/c2pa-web';

import { VideogularComponent } from '../videogular/Videogular.Component';
import { ITimeMarker } from '../videogular/controls/VbTimeMarkers.Component';
import { ActiveStats, C2PA_INVALID_ICON, C2PA_VERIFIED_ICON } from './IVbC2paContract';

import { IVbSource } from '../IVbSource';
import { HlsUtilService } from '../HlsUtil.Service';
import { VgConferenceDualHlsService } from '../../hls/dual/VgConferenceDualHls.Service';

@Component({
	selector: 'vb-c2pa',
	standalone: true,
	imports: [
		CommonModule,
		BsDropdownModule,
		TooltipModule
	],
	template: `
		@if(showC2pa() && (isInvalidAsset || !isInvalidAsset && controlSurfaced)) {
			 <div dropdown #bsDropdown class="dropdown" [insideClick]="true">
				<button
					class="dropdown-toggle c2paBtn"
					[attr.aria-label]="translations.c2paClickToViewDetails"
					type="button"
					dropdownToggle>
					<img [src]="!isInvalidAsset ? C2PA_VERIFIED_ICON : C2PA_INVALID_ICON" [alt]="!isInvalidAsset ? translations.c2paValid : translations.c2paInvalid" class="c2paLogo" />
				</button>
				<ul
					*dropdownMenu
					#vbDropdownMenu
					class="dropdownMenu dropdown-menu keep-border-radius">
					<li class="dropdown-item headerItem">
						<div class="title">
							<div>
								{{translations.c2paContentCredentials}}

								@if (isInvalidAsset) {
									<span class="invalid">({{translations.invalid}})</span>
								}
								<span tabindex="0" class="glyphicons circle_info c2paTooltip" [tooltip]="translations.c2paTooltip" container="body"></span>
							</div>
							<div class="subtitle">{{ signatureInfo()?.issuer || 'Unknown' }}
							 / {{ signatureInfo()?.time | date:'mediumDate' }}</div>
						</div>
					</li>
					@if (vgApi?.c2paConfig?.viewMoreClickHandler) {
						<li class="dropdown-item item">
							<button
								class="viewMoreBtn"
								type="button"
								(click)="vgApi.c2paConfig?.viewMoreClickHandler?.()">
								{{ translations.viewMore }}
							</button>
						</li>
					}
				</ul>
			</div>
		}
	`,
	styleUrls: ['./C2PA.Component.ng.less']
})
export class VbC2PAComponent implements AfterViewInit, OnDestroy, OnChanges {
	@Input() public activeSource: IVbSource;
	@Input() public controlSurfaced: boolean;
	@Input() public translations: any;

	@Output() public failedC2paTimeMarkers = new EventEmitter<ITimeMarker[]>();

	public videoStats = signal({} as ActiveStats);
	public audioStats = signal({} as ActiveStats);
	public contentVideoStats = signal({} as ActiveStats);
	public showC2pa:Signal<boolean> = signal(false);
	public signatureInfo:Signal<SignatureInfo> = signal({} as SignatureInfo);

	public readonly C2PA_VERIFIED_ICON = C2PA_VERIFIED_ICON;
	public readonly C2PA_INVALID_ICON = C2PA_INVALID_ICON;
	public readonly;
	public isInvalidAsset;
	private c2paStatReporterInstance;
	private subscription: Subscription;

	constructor(
		public vgApi: VideogularComponent,
		private HlsUtilService: HlsUtilService,
		private VgConferenceDualHlsService: VgConferenceDualHlsService
	) {}


	public ngOnChanges(changes: SimpleChanges) {
		if (changes.activeSource) {
			this.initC2paStatsReporter();
		}
	}

	public ngAfterViewInit() {
		if(!this.vgApi?.c2paConfig) {
			return;
		}
		this.showC2pa = computed(() => {
			return !!(this.videoStats()?.activeSegmentStats || this.audioStats()?.activeSegmentStats);
		});

		this.signatureInfo = computed(() => {
			if(this.showC2pa()) {
				const activeSegmentStats = this.videoStats()?.activeSegmentStats || this.audioStats()?.activeSegmentStats;
				const manifestId = activeSegmentStats?.manifestStore?.active_manifest;
				const manifest = activeSegmentStats?.manifestStore?.manifests?.[manifestId];

				return manifest?.signature_info;
			}
		});
	}

	private initC2paStatsReporter(): void {
		if(!this.vgApi?.c2paConfig) {
			return;
		}
		const { src, type } = this.activeSource || {};
		import('./VbC2paStatsReporter').then(({ c2paStatReporterInstance }) => {
			this.c2paStatReporterInstance = c2paStatReporterInstance;
			this.c2paStatReporterInstance.destroy();

			//only incase of hls player current time needs to be fed to stats reporter.
			if (this.HlsUtilService.isHLS(src, type) || this.VgConferenceDualHlsService.isPlaybackSupported(this.activeSource)) {
				this.c2paStatReporterInstance.initPlayerCurrentTimeObservable(this.vgApi.currentTime$);
			}

			this.subscription = c2paStatReporterInstance.currentStats$.subscribe(stats => {
				this.updateC2paStats(stats);
			});
		});
	}

	private updateC2paStats(stats: ActiveStats): void {
		if (!stats) {
			this.videoStats.set({} as ActiveStats);
			this.audioStats.set({} as ActiveStats);
			this.contentVideoStats.set({} as ActiveStats);
		}

		if (stats?.type === 'video') {
			this.videoStats.set(stats);
		} else if (stats?.type === 'audio') {
			this.audioStats.set(stats);
		} else if (stats?.type === 'contentVideo') {
			this.contentVideoStats.set(stats);
		}

		this.isInvalidAsset = stats && !stats.overallVerified;

		this.checkAndEmitFailedC2paTimeMarkers();

		this.vgApi.updateC2paStats({
			video: this.videoStats(),
			audio: this.audioStats(),
			contentVideo: this.contentVideoStats()
		});
	}

	public ngOnDestroy() {
		this.c2paStatReporterInstance.destroy();
		this.subscription?.unsubscribe();
	}

	private checkAndEmitFailedC2paTimeMarkers(): void {
		if (this.isInvalidAsset) {
			const failedVideos = this.videoStats()?.errorSegments || [];
			const failedAudios = this.audioStats()?.errorSegments || [];
			const failedContentVideos = this.contentVideoStats()?.errorSegments || [];
			const failedSegments = [...failedVideos, ...failedAudios, ...failedContentVideos];
			const timeMarkers: ITimeMarker[] = failedSegments.map(seg => ({
				startTime: seg.interval[0],
				endTime: seg.interval[1],
				cssClassName: 'failedC2paMarker', //Todo test it later
				markerOnScrubBar: true,
			}));
			this.failedC2paTimeMarkers.emit(timeMarkers);
		}
	}
}
